import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  responsiveFontSizes,
  CssBaseline,
  useMediaQuery,
} from "@mui/material";
import theme from "./theme";

// Create theme context
const ThemeContext = createContext({
  mode: "light",
  toggleMode: () => {},
  primaryColor: "#2563eb",
  setPrimaryColor: () => {},
});

/**
 * Get preferred color scheme from system
 *
 * @returns {string} "light" or "dark"
 */
const getPreferredColorScheme = () => {
  if (typeof window === "undefined") return "light";

  // Check local storage first
  const storedMode = localStorage.getItem("theme-mode");
  if (storedMode) return storedMode;

  // Fall back to system preference
  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

/**
 * Custom theme provider component
 *
 * @param {Object} props Component properties
 * @param {ReactNode} props.children Child components
 * @returns {JSX.Element} Theme provider
 */
const ThemeProvider = ({ children }) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

export default ThemeProvider;

/**
 * Custom hook to use theme context
 *
 * @returns {Object} Theme context
 */
export function useThemeContext() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }

  return context;
}

/**
 * Theme toggle button component
 *
 * @param {Object} props Component properties
 * @param {JSX.Element} props.children Custom toggle element
 * @returns {JSX.Element} Theme toggle button
 */
export function ThemeToggle({ children }) {
  const { mode, toggleMode } = useThemeContext();

  return (
    <div onClick={toggleMode} style={{ cursor: "pointer" }}>
      {children || (mode === "light" ? "Dark Mode" : "Light Mode")}
    </div>
  );
}
